import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {WhatToBringOverlay} from '~/ViewInvitation/common/WhatToBringOverlay';
import {fetchWhatToBring, postWhatToBringSignup} from '~/ViewInvitation/reducers/apiActions';
import {isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {DeleteBinLineIcon} from '~library/svg/DeleteBinLineIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {WhatToBringLineIcon} from '~library/svg/WhatToBringLineIcon';
import {largishAndUp, link} from '~libSass/base/_exports.sass';

export const WhatToBringModule = (props) => {
  const {restored, onPropagateRestore, readonly} = props;
  const dispatch = useDispatch();
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    let eventId;
    if (props.store) {
      eventId = props.store?.getState()?.eviteEvent?.id;
    } else {
      eventId = window.event_id || window.evite_event.id;
    }
    dispatch(fetchWhatToBring(eventId));
  }, []);

  const {items} = useSelector((state) => state.whatToBring);
  const {userId, isHost, name} = useSelector((state) => state.user);
  const {eventId, widgets} = useSelector((state) => state.eventDetails);
  const {wtbListId} = useSelector(({whatToBringSummary: wtbs}) => ({
    wtbListId: wtbs.list_id,
  }));

  const hasRSVPd = useSelector((state) => state.rsvp?.hasRsvpd ?? false);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const onOpenWhatToBring = () => {
    setOverlayOpen(true);
  };

  const onAssign = async (userId, guestName, itemId, quantity) => {
    const item = items.find((i) => i.id === itemId);

    if (!item) {
      throw new Error('An error occurred during assignments.');
    }

    return new Promise((resolve, reject) => {
      dispatch(
        postWhatToBringSignup(eventId, wtbListId, itemId, quantity, {
          userId,
          userName: guestName,
        })
      )
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const onUnassign = async (userId, guestName, itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const alreadyAssigned = item.selections.map((s) => ({
      ...s.user,
      quantity: s.quantity,
    }));

    const user = alreadyAssigned.find((a) => a.id === userId);
    if (!user) return;

    const quantity = 0;

    return new Promise((resolve, reject) => {
      dispatch(
        postWhatToBringSignup(eventId, wtbListId, itemId, quantity, {
          userId,
          userName: guestName,
        })
      )
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const preview = isPreview();

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (restored && preview && isHost) {
      // API call to restore module
      const patchedEventData = {
        widgets,
      };
      onPropagateRestore(patchedEventData);
    }
  }, [restored]);

  return (
    <>
      {preview && !readonly && (
        <div className="view-invitation__preview-cta multi light-bg">
          <a
            className="view-invitation__preview-cta light-bg"
            href={`/invitation/${eventId}/details`}
            data-qa-id="previewCtaEdit"
            ref={(elem) => {
              if (props.connectCTA) props.connectCTA('edit-wtb', elem);
            }}
          >
            <EditLineIcon ratio={0.5} color={link} />
            Edit
          </a>
          <span>|</span>
          <button
            type="button"
            className="unstyled-button view-invitation__preview-cta light-bg"
            ref={(elem) => props.connectCTA && props.connectCTA('remove-wtb', elem)}
            data-qa-id="previewCtaRemove"
          >
            <DeleteBinLineIcon ratio={0.5} color={link} />
            Remove
          </button>
        </div>
      )}
      <div className="view-invitation__what-to-bring-module">
        <div className="view-invitation__what-to-bring__main-contents">
          <div className="view-invitation__title-with-icon-wrapper view-invitation__what-to-bring__title">
            <WhatToBringLineIcon ratio={0.75} />
            <Typography variant="header3" data-qa-id="what-to-bring-module-title">
              What to bring
            </Typography>
          </div>
          <Typography
            variant="paragraph3"
            color="pebble"
            classNames="view-invitation__what-to-bring__subheading"
          >
            Help your host out! View their What to Bring list and sign up for your choice now.
          </Typography>
        </div>
        <Button
          data-qa-id={hasRSVPd ? 'view-their-list' : 'rsvp-to-contribute'}
          variant="primary"
          size={largeScreens ? 'large' : 'small'}
          onClick={onOpenWhatToBring}
          noTextWrap
          disabled={preview || !hasRSVPd}
        >
          {hasRSVPd || isHost ? 'View List' : 'RSVP to View Their List'}
        </Button>
      </div>
      {!preview &&
        createPortal(
          <WhatToBringOverlay
            open={overlayOpen}
            onClose={() => setOverlayOpen(false)}
            items={items}
            userId={userId}
            name={name}
            isHost={isHost}
            hasRsvpd={hasRSVPd}
            onAssign={onAssign}
            onUnassign={onUnassign}
          />,
          document.body
        )}
    </>
  );
};
